<template>
  <div class="app-container">
  <div class="app-header">
      <div class="logo" @click="handleLogoClick"></div>
      <div class="header-actions">
        <v-btn
          color="primary"
          variant="text"
          icon="mdi-logout"
          :title="'Logout'"
          @click="logout"
        />
      </div>
      <div class="workshop-info" v-if="workshopData">
        <span v-if="workshopData.companyName">Company {{workshopData.companyName}} ⁃ </span>
        <span>Initiative {{workshopData.name}}</span>
      </div>
    </div>
    <div class="app-content">
      <!-- Tabs for navigation (now at the very top) -->
      <v-tabs v-model="tab" background-color="primary" dark grow class="mb-6">
        <v-tab :value="0">Initiative Chart</v-tab>
        <v-tab :value="1">Topic Selection</v-tab>
        <v-tab v-if="riskAssessmentEnabled" :value="2">Risk Assessment</v-tab>
        <v-tab :value="3">Improvements</v-tab>
        <v-tab :value="4">Tasks</v-tab>
      </v-tabs>
      <v-window v-model="tab" class="tab-window">
        <!-- Initiative Chart Tab -->
        <v-window-item :value="0">
          <workshop-chart ref="workshopChart"
            :customerId="customerId"
            :workshopId="workshopId"
            :departmentId="departmentId"
            :projectId="projectId"
            :riskAssessmentEnabled="riskAssessmentEnabled"
            :llmFunctionalityEnabled="llmFunctionalityEnabled"
            @initiative-updated="onInitiativeUpdated"
            @project-description-updated="onProjectDescriptionUpdated"
            @update:riskAssessmentEnabled="onRiskAssessmentToggle"
            @update:llmFunctionalityEnabled="onLlmFunctionalityToggle"
          />
        </v-window-item>
        <!-- Topic Selection Tab -->
        <v-window-item :value="1">
          <template v-if="workshopData">
            <workshop-topic-selection 
              ref="topicSelection" 
              :customerId="customerId"
              :workshopId="workshopId"
              :departmentId="departmentId"
              :projectId="projectId"
              :workshopData="workshopData"
              @selection-updated="updateSelection"
            />
          </template>
          <template v-else>
            <v-alert type="info" class="mt-4">Please conduct an initiative first.</v-alert>
          </template>
        </v-window-item>
        <!-- Risk Assessment Tab -->
        <v-window-item v-if="riskAssessmentEnabled" :value="2">
          <template v-if="selection && selection.length">
            <risk-assessment-matrix
              ref="riskAssessment"
              :customerId="customerId"
              :workshopId="workshopId"
              :departmentId="departmentId"
              :projectId="projectId"
              :selectedTopics="selection"
            />
          </template>
          <template v-else>
            <v-alert type="info" class="mt-4">Please select topics and conduct voting first.</v-alert>
          </template>
        </v-window-item>
        <!-- Results Tab -->
        <v-window-item :value="3">
          <template v-if="selection && selection.length">
            <result-table 
              ref="resultTable" 
              :customerId="customerId"
              :riskAssessmentEnabled="riskAssessmentEnabled"
              :workshopId="workshopId"
              :departmentId="departmentId"
              :projectId="projectId"
              :workshopData="workshopData"
              :selection="selection"
              :llmFunctionalityEnabled="llmFunctionalityEnabled"
              :projectDescription="projectDescription"
            />
          </template>
          <template v-else>
            <v-alert type="info" class="mt-4">Please complete the risk assessment first.</v-alert>
          </template>
        </v-window-item>
        <!-- Tasks Tab -->
        <v-window-item :value="4">
          <TaskView 
            :customerId="customerId"
            :workshopId="workshopId"
            :departmentId="departmentId"
            :projectId="projectId"
          />
        </v-window-item>
      </v-window>
    </div>
  </div>
</template>

<script>
import WorkshopChart from "@/components/workshopChart.vue";
import WorkshopTopicSelection from "@/components/workshopTopicSelection.vue";
import ResultTable from "@/components/resultTable.vue";
import TaskView from "@/views/TaskView.vue";
import RiskAssessmentMatrix from "@/components/quality/RiskAssessmentMatrix.vue";
import {loadWorkshop} from "@/workshop/workshopFunctions";
import { getDatabase, ref as dbRef, set, get, onValue } from "firebase/database";
import { getAuth, signOut } from "firebase/auth";

export default {
  name: "WorkshopView",
  components: {
    TaskView, 
    ResultTable, 
    WorkshopTopicSelection, 
    WorkshopChart,
    RiskAssessmentMatrix
  },
  data() {
    return {
      tab: 0,
      selection: [],
      workshopData: {},
      started: false,
      qrdialog: false,
      userDialog: false,
      filterDialog: false,
      taskDialogShow: false,
      riskAssessment: {},
      riskAssessmentEnabled: true,
      llmFunctionalityEnabled: true,
      projectDescription: '',
    }
  },
  computed: {
    customerId() { return this.$route.params.customerId; },
    departmentId() { return this.$route.params.departmentId; },
    projectId() { return this.$route.params.projectId; },
    workshopId() { return this.$route.params.id; },
    isAdmin() {
      const user = this.$store.getters.userProfile;
      const adminRoles = ['super_admin', 'company_admin', 'department_admin', 'project_admin'];
      return !!(user && adminRoles.includes(user.role));
    }
  },
  mounted() {
    this.$store.dispatch('saveCustomerId', this.customerId);
    this.$store.dispatch('saveWorkshopId', this.workshopId);
    this.loadInitialData();
    this.loadRiskAssessmentToggle();
    // Keep header title in sync with live initiative name changes
    const db = getDatabase();
    const nameRef = dbRef(db, `customers/${this.customerId}/workshop/${this.workshopId}/name`);
    onValue(nameRef, (snap) => {
      const n = snap.val();
      if (n) {
        this.workshopData = { ...(this.workshopData || {}), name: n };
      }
    });
    window.scrollTo({ top: 0, behavior: 'auto' });
  },
  methods: {
    handleLogoClick() {
      const user = this.$store.getters.userProfile;
      const role = user?.role;
      const adminRoles = ['super_admin', 'company_admin', 'department_admin', 'project_admin'];
      if (adminRoles.includes(role)) {
        this.$router.push('/chart');
      }
      // Non-admin: stay on current initiative
    },
    async loadInitialData() {
      try {
        const res = await loadWorkshop(this.customerId, this.workshopId);
        if (!res) return;
        this.workshopData = res;
        if (res.selectedTopics) {
          this.selection = [...res.selectedTopics];
        }
        if (res.projectDescription) {
          this.projectDescription = res.projectDescription;
        }
      } catch (error) {
        console.error('Error loading initial workshop data:', error);
      }
    },
    updateSelection(selectedIds) {
      this.selection = [...selectedIds];
    },
    onInitiativeUpdated() {
      if (this.$refs.workshopChart && this.$refs.workshopChart.reloadWorkshopData) {
        this.$refs.workshopChart.reloadWorkshopData();
      }
      this.loadInitialData();
    },
    onProjectDescriptionUpdated(desc) {
      this.projectDescription = desc;
    },
    async loadRiskAssessmentToggle() {
      const db = getDatabase();
      const toggleRef = dbRef(db, `customers/${this.customerId}/workshop/${this.workshopId}/riskAssessmentEnabled`);
      const snap = await get(toggleRef);
      if (snap.exists()) {
        this.riskAssessmentEnabled = snap.val();
      } else {
        this.riskAssessmentEnabled = true; // default
      }
      // load LLM toggle as well
      const llmRef = dbRef(db, `customers/${this.customerId}/workshop/${this.workshopId}/llmEnabled`);
      const llmSnap = await get(llmRef);
      if (llmSnap.exists()) {
        this.llmFunctionalityEnabled = !!llmSnap.val();
      }
    },
    async updateRiskAssessment() {
      const db = getDatabase();
      const toggleRef = dbRef(db, `customers/${this.customerId}/workshop/${this.workshopId}/riskAssessmentEnabled`);
      await set(toggleRef, this.riskAssessmentEnabled);
    },
    onRiskAssessmentToggle(newValue) {
      this.riskAssessmentEnabled = newValue;
      this.updateRiskAssessment();
    },
    onLlmFunctionalityToggle(newValue) {
      this.llmFunctionalityEnabled = newValue;
      const db = getDatabase();
      const llmRef = dbRef(db, `customers/${this.customerId}/workshop/${this.workshopId}/llmEnabled`);
      set(llmRef, !!newValue);
    },
    async logout() {
      try {
        const auth = getAuth();
        await signOut(auth);
      } catch (e) {
        // ignore, proceed to clear session anyway
      }
      try { sessionStorage.clear(); } catch (e) { /* ignore */ }
      this.$store.commit('setUser', null);
      this.$store.commit('setUserProfile', null);
      this.$router.push('/login');
    }
  }
}
</script>

<style>
.app-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.app-header {
  background: white;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 80px;
}

.logo {
  position: absolute;
  top: 0;
  left: 0;
  width: 300px;
  height: 80px;
  background-image: url('../assets/Logo_Text.png');
  background-repeat: no-repeat;
  background-size: contain;
}

.header-actions {
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 2000;
}

.workshop-info {
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
  margin: 0 24px;
}

.app-content {
  margin-top: 82px;
  padding: 24px;
  flex-grow: 1;
}
</style>